import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPostsByCategory } from '../services/blogService';
import type { BlogPost, PageContent } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useContent } from '../hooks/useContent';
import { AdminControls } from '../components/AdminControls';
import { useToast } from '../hooks/useToast';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../src/firebase';
import DOMPurify from 'dompurify';
import { linkifyHtml } from '../utils/linkify';
import { updateMetaTag, updateLinkTag, updateRobotsTag, updateCustomHead } from '../hooks/useSeo';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const BlogCategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  
  const { isAdminMode } = useContent();
  const { showToast } = useToast();
  const decodedCategory = category ? decodeURIComponent(category) : '';

  const defaultContent: PageContent = {
    h1: `Category: ${decodedCategory}`,
    body: '',
    seo: {
      title: `${decodedCategory} Blog Posts - BrandGenius`,
      description: `Articles and insights about ${decodedCategory}.`,
      canonical: `/blog/category/${category}/`,
      slug: `/blog/category/${category}/`,
      customHead: '',
      noindex: false
    }
  };

  const [pageContent, setPageContent] = useState<PageContent>(defaultContent);

  useEffect(() => {
    const fetchCategoryContent = async () => {
      if (category) {
        try {
          const docRef = doc(db, 'appContent', `blogCategory_${category}`);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setPageContent(docSnap.data() as PageContent);
          } else {
            setPageContent(defaultContent);
          }
        } catch (e) {
          console.error("Error fetching category content", e);
        }
      }
    };
    fetchCategoryContent();
  }, [category]);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      if (category) {
        const fetchedPosts = await getBlogPostsByCategory(category);
        setPosts(fetchedPosts);
      }
      setLoading(false);
    };
    fetchPosts();
  }, [category]);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('app-rendered'));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, posts]);

  const handleSave = async () => {
    if (category) {
      try {
        const docRef = doc(db, 'appContent', `blogCategory_${category}`);
        await setDoc(docRef, pageContent);
        showToast('Category content saved!');
      } catch (e) {
        console.error("Error saving category content", e);
        showToast('Error saving content', 'error');
      }
    }
  };

  const sanitizedBody = pageContent?.body ? linkifyHtml(DOMPurify.sanitize(pageContent.body, { ADD_ATTR: ['class', 'target', 'rel'] })) : '';

  useIsomorphicLayoutEffect(() => {
    if (pageContent) {
      document.title = pageContent.seo.title;
      updateMetaTag('name', 'description', pageContent.seo.description);
      if (pageContent.seo.canonical) {
        updateLinkTag('canonical', `https://www.brandgenius.app${pageContent.seo.canonical}`);
      }
      updateRobotsTag(pageContent.seo.noindex);
      
      if (pageContent.seo.customHead) {
        updateCustomHead(`<style type="text/css">${pageContent.seo.customHead}</style>`);
      }
    }
  }, [pageContent]);

  return (
    <>
      <div className="w-full max-w-6xl mx-auto py-12 md:py-20 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
        {isAdminMode && (
          <AdminControls 
            pageId={`blogCategory_${category}`} 
            pageContent={pageContent} 
            onContentChange={setPageContent}
            onSave={handleSave}
          />
        )}
        <div className="w-full max-w-3xl mx-auto mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 mb-6">
            {pageContent.h1.includes('Category:') ? (
              <>Category: <span className="text-indigo-600">{pageContent.h1.replace('Category: ', '').replace('Category:', '')}</span></>
            ) : (
              pageContent.h1
            )}
          </h1>
          {sanitizedBody && (
            <div className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-none mx-auto mb-6" dangerouslySetInnerHTML={{ __html: sanitizedBody }} />
          )}
          <Link to="/blog/" className="text-indigo-600 hover:text-indigo-800 font-medium">&larr; Back to all posts</Link>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-10 text-gray-500">No blog posts found for this category.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map(post => (
              <div key={post.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition-shadow duration-300">
                {post.featuredImage && (
                  <Link to={`/blog/${post.slug}/`}>
                    <img src={post.featuredImage} alt={post.title} className="w-full h-48 object-cover" />
                  </Link>
                )}
                <div className="p-6 flex flex-col flex-grow">
                  <Link to={`/blog/${post.slug}/`} className="block mb-2 mt-2">
                    <h2 className="text-xl font-bold text-gray-900 hover:text-indigo-600 transition-colors line-clamp-2">{post.title}</h2>
                  </Link>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between text-xs text-gray-500">
                    <span>{post.author}</span>
                    <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
